import { FieldPath, UseFormReturn } from 'react-hook-form';
import { ToggleField } from '@brighte/form-components';
import { HouseholdType, Period } from '@brighte/validation';
import { RepaymentField } from '@components/RepaymentField';
import { Stack } from '@spark-web/stack';
import { FinancialFieldTypes, HOUSEHOLD, isFieldExists } from '@utils';

type Props = Pick<UseFormReturn<HouseholdType>, 'watch' | 'control' | 'setValue'> & {
  additionalFields: string[];
};

function InvestmentProperties({ watch, control, setValue, additionalFields }: Props) {
  const {
    INVESTMENT_PROPERTIES_LABEL,
    INVESTMENT_PROPERTIES_SUBTITLE,
    INVESTMENT_PROPERTIES_FREQUENCY_LABEL,
    INVESTMENT_PROPERTIES_FREQUENCY_DESCRIPTION,
  } = HOUSEHOLD;

  const INVESTMENT_PROPERTIES_FIELD = 'applicant.expense.investmentProperties' as FieldPath<HouseholdType>;

  const [isInvestmentPropertiesNotApplicable, investmentPropertiesAmount, investmentPropertiesPeriod] = watch([
    `${INVESTMENT_PROPERTIES_FIELD}.isNotApplicable`,
    `${INVESTMENT_PROPERTIES_FIELD}.amount`,
    `${INVESTMENT_PROPERTIES_FIELD}.period`,
  ]);

  const hasIsNotApplicable = isFieldExists(additionalFields, `${INVESTMENT_PROPERTIES_FIELD}.isNotApplicable`);

  const handleInvestmentPropertiesToggle = (value: boolean) => {
    if (value) {
      setValue(`${INVESTMENT_PROPERTIES_FIELD}.amount` as FieldPath<HouseholdType>, 0);
      setValue(`${INVESTMENT_PROPERTIES_FIELD}.period` as FieldPath<HouseholdType>, Period.Monthly);
    }
  };

  if (!hasIsNotApplicable) {
    return (
      <RepaymentField
        control={control}
        periodFieldName={`${INVESTMENT_PROPERTIES_FIELD}.period` as FieldPath<HouseholdType>}
        amountFieldName={`${INVESTMENT_PROPERTIES_FIELD}.amount` as FieldPath<HouseholdType>}
        periodValue={investmentPropertiesPeriod}
        amountValue={investmentPropertiesAmount}
        financialFieldType={FinancialFieldTypes.INVESTMENT_PROPERTIES}
        disabled={isInvestmentPropertiesNotApplicable}
        gap="xxlarge"
        heading={INVESTMENT_PROPERTIES_FREQUENCY_LABEL}
        subheading={INVESTMENT_PROPERTIES_FREQUENCY_DESCRIPTION}
      />
    );
  }

  return (
    <Stack gap="xxlarge">
      <ToggleField
        name={`${INVESTMENT_PROPERTIES_FIELD}.isNotApplicable` as FieldPath<HouseholdType>}
        label={INVESTMENT_PROPERTIES_LABEL}
        description={INVESTMENT_PROPERTIES_SUBTITLE}
        control={control}
        layout="inline"
        onChange={handleInvestmentPropertiesToggle}
        isInvertedValue
      />

      {!isInvestmentPropertiesNotApplicable && isInvestmentPropertiesNotApplicable !== undefined && (
        <RepaymentField
          control={control}
          periodFieldName={`${INVESTMENT_PROPERTIES_FIELD}.period` as FieldPath<HouseholdType>}
          amountFieldName={`${INVESTMENT_PROPERTIES_FIELD}.amount` as FieldPath<HouseholdType>}
          periodValue={investmentPropertiesPeriod}
          amountValue={investmentPropertiesAmount}
          financialFieldType={FinancialFieldTypes.INVESTMENT_PROPERTIES}
          heading={INVESTMENT_PROPERTIES_FREQUENCY_LABEL}
          subheading={INVESTMENT_PROPERTIES_FREQUENCY_DESCRIPTION}
        />
      )}
    </Stack>
  );
}

export default InvestmentProperties;
